import React, {startTransition, useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {DetailPageFrame, DetailSection} from '@/components/football';
import PlayerInfo from '@/components/players/PlayerInfo';
import PlayerReportAction from '@/components/players/PlayerReportAction';
import PlayerTabs from '@/components/players/PlayerTabs';
import WyscoutPlayerMatchDrawer from '@/components/players/WyscoutPlayerMatchDrawer';
import ConfirmModal from '@/components/ui/ConfirmModal';
import {useToast} from '@/context/ToastContext';
import {useWorkspace} from '@/context/WorkspaceContext';
import {PlayerAvailability, PlayerDetail, playersV2API} from '@/features/players/api';
import {parsePlayerReturnTo, parsePlayerTab} from '@/features/players/playerUrlState';
import {playerKeys} from '@/features/players/queries';
import {PlayerTab} from '@/features/players/types';
import {usePlayerRealtime} from '@/features/players/usePlayerRealtime';
import {wyscoutFixtureAPI, wyscoutFixtureKeys} from '@/features/players/wyscoutFixtureApi';
import {useWyscoutFixtureRefreshAnnouncement} from '@/features/players/useWyscoutFixtureRefreshAnnouncement';
import {getV2Error, V2DetailResponse} from '@/services/api/v2';
import ScoutingModeSections from './PlayerDetailPage/ScoutingModeSections';
import SquadModeSections from './PlayerDetailPage/SquadModeSections';

interface PlayerDetailPageProps { mode?: 'squad' | 'scouting' }

interface WatchVariables {
  workspaceId: string;
  playerId: string;
  playerName: string;
  isWatched: boolean;
}

interface WatchMutationContext {
  previous?: V2DetailResponse<PlayerDetail>;
}

interface ActionError {
  action: 'availability' | 'delete' | 'watchlist';
  workspaceId: string;
  playerId: string;
  message: string;
}

const actionClass = 'inline-flex min-h-11 items-center rounded-md border border-gray-300 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800';
const availabilityClasses: Record<PlayerAvailability, string> = {
  fit: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
  injured: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  recovering: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  questionable: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
};

const PlayerDetailPage: React.FC<PlayerDetailPageProps> = ({mode = 'squad'}) => {
  const {id = ''} = useParams<{id: string}>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {currentWorkspace} = useWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';
  const mountedRef = useRef(true);
  const activeContextRef = useRef({workspaceId, playerId: id});
  activeContextRef.current = {workspaceId, playerId: id};
  const contextIsActive = (variables: WatchVariables) =>
    mountedRef.current
    && activeContextRef.current.workspaceId === variables.workspaceId
    && activeContextRef.current.playerId === variables.playerId;
  usePlayerRealtime(workspaceId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {showToast} = useToast();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [actionError, setActionError] = useState<ActionError>();
  const [pendingWatchKeys, setPendingWatchKeys] = useState<ReadonlySet<string>>(() => new Set());
  const openerRef = useRef<HTMLElement | null>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const lastSelectedMatchId = useRef<number | null>(null);
  const root = mode === 'squad' ? '/players' : '/scouting';
  const backHref = parsePlayerReturnTo(searchParams.get('from'), mode);
  const detail = useQuery({
    queryKey: playerKeys.detail(workspaceId, id),
    queryFn: () => playersV2API.detail<PlayerDetail>(id, workspaceId),
    enabled: Boolean(workspaceId && id),
  });
  const allowedTabs: PlayerTab[] = detail.data?.data.tabs ?? ['overview'];
  const activeTab = parsePlayerTab(searchParams.get('tab'), allowedTabs);
  const matchValues = searchParams.getAll('wyscout_match');
  const rawMatchId = matchValues.length === 1 ? matchValues[0] : null;
  const parsedMatchId = rawMatchId && /^[1-9]\d*$/.test(rawMatchId) ? Number(rawMatchId) : null;
  const selectedMatchId = parsedMatchId !== null && Number.isSafeInteger(parsedMatchId) ? parsedMatchId : null;
  const invalidMatchId = matchValues.length > 0 && selectedMatchId === null;
  const fixtureDetail = useQuery({
    queryKey: wyscoutFixtureKeys.detail(workspaceId, id, selectedMatchId ?? 0),
    queryFn: ({signal}) => wyscoutFixtureAPI.detail(workspaceId, id, selectedMatchId as number, signal),
    enabled: Boolean(workspaceId && id && selectedMatchId),
    retry: false,
  });
  const fixtureRefreshAnnouncement = useWyscoutFixtureRefreshAnnouncement(
    selectedMatchId === null ? null : `${workspaceId}:${id}:${selectedMatchId}`,
    fixtureDetail.fetchStatus,
    fixtureDetail.isError,
    fixtureDetail.dataUpdatedAt,
    fixtureDetail.data,
  );
  const fixtureErrorCode = fixtureDetail.isError ? getV2Error(fixtureDetail.error).code : null;
  const fixtureAccessInvalidated = fixtureErrorCode === 'forbidden' || fixtureErrorCode === 'not_found';

  const removeSelectedMatch = (replace = false) => {
    const next = new URLSearchParams(searchParams);
    next.delete('wyscout_match');
    setSearchParams(next, {replace});
  };

  useEffect(() => {
    if (selectedMatchId !== null) lastSelectedMatchId.current = selectedMatchId;
  }, [selectedMatchId]);

  useEffect(() => {
    if (!invalidMatchId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('wyscout_match');
    setSearchParams(next, {replace: true});
  }, [invalidMatchId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedMatchId || !fixtureAccessInvalidated) return;
    const next = new URLSearchParams(searchParams);
    next.delete('wyscout_match');
    setSearchParams(next, {replace: true});
  }, [fixtureAccessInvalidated, searchParams, selectedMatchId, setSearchParams]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!detail.data || searchParams.get('tab') === null || searchParams.get('tab') === activeTab) return;
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, {replace: true});
  }, [activeTab, detail.data, searchParams, setSearchParams]);

  const remove = useMutation({
    mutationFn: () => playersV2API.remove(id, workspaceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: playerKeys.all(workspaceId)});
      showToast('Player deleted', 'success');
      startTransition(() => navigate(backHref, {replace: true}));
    },
    onError: error => {
      setConfirmingDelete(false);
      setActionError({action: 'delete', workspaceId, playerId: id, message: getV2Error(error).message});
    },
  });
  const availability = useMutation({
    mutationFn: (value: PlayerAvailability) => playersV2API.updateAvailability(id, value, workspaceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: playerKeys.detail(workspaceId, id)});
      showToast('Availability updated', 'success');
    },
    onError: error => setActionError({action: 'availability', workspaceId, playerId: id, message: getV2Error(error).message}),
  });
  const watch = useMutation<
    {data: {id: string; is_watched: boolean}},
    unknown,
    WatchVariables,
    WatchMutationContext
  >({
    mutationFn: variables => playersV2API.setWatched(
      variables.playerId, variables.isWatched, variables.workspaceId,
    ),
    onMutate: async variables => {
      setPendingWatchKeys(current => new Set(current).add(`${variables.workspaceId}:${variables.playerId}`));
      if (contextIsActive(variables)) {
        setActionError(current => current?.action === 'watchlist' ? undefined : current);
      }
      const queryKey = playerKeys.detail(variables.workspaceId, variables.playerId);
      await queryClient.cancelQueries({queryKey, exact: true});
      const previous = queryClient.getQueryData<V2DetailResponse<PlayerDetail>>(queryKey);
      queryClient.setQueryData<V2DetailResponse<PlayerDetail>>(queryKey, (current: V2DetailResponse<PlayerDetail> | undefined) => current ? ({
        ...current,
        data: {...current.data, is_watched: variables.isWatched},
      }) : current);
      return {previous};
    },
    onError: (error, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          playerKeys.detail(variables.workspaceId, variables.playerId),
          context.previous,
        );
      }
      if (contextIsActive(variables)) {
        setActionError({
          action: 'watchlist',
          workspaceId: variables.workspaceId,
          playerId: variables.playerId,
          message: getV2Error(error).message,
        });
      }
    },
    onSuccess: (_result, variables) => {
      if (!contextIsActive(variables)) return;
      showToast(
        `${variables.isWatched ? 'Added' : 'Removed'} ${variables.playerName} ${variables.isWatched ? 'to' : 'from'} Watching`,
        'success',
      );
    },
    onSettled: (_result, _error, variables) => {
      if (mountedRef.current) {
        setPendingWatchKeys(current => {
          const next = new Set(current);
          next.delete(`${variables.workspaceId}:${variables.playerId}`);
          return next;
        });
      }
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: playerKeys.detail(variables.workspaceId, variables.playerId),
          exact: true,
        }),
        queryClient.invalidateQueries({queryKey: playerKeys.all(variables.workspaceId)}),
      ]);
    },
  });

  if (detail.isPending) return <DetailSection state={{status: 'loading', message: 'Loading player...'}} />;
  if (detail.isError) return <DetailSection state={{status: 'error', message: 'Unable to load this player.', retryLabel: 'Retry player', retry: () => { void detail.refetch(); }}} />;
  if (!detail.data) return null;
  const player: PlayerDetail = detail.data.data;
  const capabilities = detail.data.capabilities;
  const detailContext = new URLSearchParams();
  if (activeTab !== 'overview') detailContext.set('tab', activeTab);
  detailContext.set('from', backHref);
  const editHref = `${root}/${id}/edit?${detailContext.toString()}`;
  const changeTab = (tab: typeof activeTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab !== 'overview') next.set('tab', tab); else next.delete('tab');
    if (!next.has('from')) next.set('from', backHref);
    setSearchParams(next);
  };
  const openFixture = (matchId: number, trigger: HTMLButtonElement) => {
    if (!Number.isSafeInteger(matchId) || matchId <= 0) return;
    openerRef.current = trigger;
    lastSelectedMatchId.current = matchId;
    const next = new URLSearchParams(searchParams);
    next.set('wyscout_match', String(matchId));
    setSearchParams(next);
  };
  const restoreFixtureFocus = () => {
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener?.isConnected) {
      opener.focus();
      return;
    }
    const fallback = backgroundRef.current?.querySelector<HTMLElement>(`[data-wyscout-match-id="${lastSelectedMatchId.current}"]`)
      ?? backgroundRef.current?.querySelector<HTMLElement>('[role="tabpanel"]');
    fallback?.focus();
  };
  const metrics = player.headline_metrics;
  const Section = mode === 'squad' ? SquadModeSections : ScoutingModeSections;
  const watchIsPending = pendingWatchKeys.has(`${workspaceId}:${player.id}`);
  const playerUrl = `${root}/${encodeURIComponent(id)}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const fullMatchSearch = new URLSearchParams({from: playerUrl});

  return (
    <>
      <div ref={backgroundRef} data-testid="player-detail-background" inert={selectedMatchId !== null}>
        <DetailPageFrame
        backHref={backHref}
        backLabel={mode === 'squad' ? 'Back to squad' : 'Back to scouting'}
        identity={<PlayerInfo player={player} />}
        status={(
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold capitalize ${availabilityClasses[player.availability]}`}>{player.availability}</span>
            {mode === 'scouting' && player.scope === 'external' && capabilities.includes('scouting.watchlist.manage') && (
              <button
                type="button"
                aria-pressed={player.is_watched}
                aria-label={watchIsPending
                  ? `Updating watching for ${player.name}`
                  : `${player.is_watched ? 'Remove' : 'Add'} ${player.name} ${player.is_watched ? 'from' : 'to'} watching`}
                disabled={watchIsPending}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-amber-500 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-wait disabled:opacity-70 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200 dark:hover:bg-amber-900"
                onClick={() => watch.mutate({
                  workspaceId,
                  playerId: player.id,
                  playerName: player.name,
                  isWatched: !player.is_watched,
                })}
              >
                <svg aria-hidden="true" className="h-5 w-5" fill={player.is_watched ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m12 3 2.78 5.63 6.22.9-4.5 4.39 1.06 6.2L12 17.2l-5.56 2.92 1.06-6.2L3 9.53l6.22-.9L12 3Z" />
                </svg>
                {watchIsPending ? 'Updating watching...' : player.is_watched ? 'Watching' : 'Add to watching'}
              </button>
            )}
            {capabilities.includes('player.availability.update') && <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Update availability<select aria-label="Update availability" disabled={availability.isPending} className="input-field ml-2 min-h-11" value={player.availability} onChange={event => availability.mutate(event.target.value as PlayerAvailability)}>{['fit', 'injured', 'recovering', 'questionable'].map(value => <option key={value} value={value}>{value}</option>)}</select></label>}
            {actionError?.workspaceId === workspaceId && actionError.playerId === id && <span role="alert" className="text-sm text-red-600">{actionError.message}</span>}
          </div>
        )}
        metrics={(
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div><dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Latest rating</dt><dd className="text-xl font-bold text-gray-900 dark:text-gray-100">{metrics.rating_source ? metrics.latest_rating : 'Not rated'}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Rating source</dt><dd className="font-semibold capitalize text-gray-900 dark:text-gray-100">{metrics.rating_source || 'None'}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Reports</dt><dd className="font-semibold text-gray-900 dark:text-gray-100">{metrics.report_count}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Last report</dt><dd className="font-semibold text-gray-900 dark:text-gray-100">{metrics.last_report_at ? new Date(metrics.last_report_at).toLocaleDateString('en-GB') : 'None'}</dd></div>
          </dl>
        )}
        tabs={<PlayerTabs tabs={allowedTabs} active={activeTab} onChange={changeTab} />}
        actions={(
          <>
            {capabilities.includes('player.compare') && <Link className={actionClass} to={`/comparison?player=${encodeURIComponent(id)}`}>Compare player</Link>}
            {capabilities.includes('player.update') && <Link className={actionClass} to={editHref}>Edit player</Link>}
            <PlayerReportAction playerId={id} playerRouteRoot={root} capabilities={capabilities} />
            {capabilities.includes('player.delete') && <button type="button" className={`${actionClass} border-red-300 text-red-700 dark:border-red-800 dark:text-red-300`} onClick={() => setConfirmingDelete(true)}>Delete player</button>}
          </>
        )}
      >
        {allowedTabs.map(tab => (
          <div key={tab} id={`player-panel-${tab}`} role="tabpanel" aria-labelledby={`player-tab-${tab}`} tabIndex={tab === activeTab ? 0 : -1} hidden={tab !== activeTab} className="min-w-0 break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500">
            {tab === activeTab && <Section key={`${workspaceId}:${id}:${tab}`} player={player} playerId={id} tab={tab} capabilities={capabilities} selectedMatchId={selectedMatchId} onOpenFixture={openFixture} />}
          </div>
        ))}
        </DetailPageFrame>
        <ConfirmModal isOpen={confirmingDelete} title="Delete player?" message={`Permanently delete ${player.name}?`} confirmLabel="Delete player permanently" tone="danger" loading={remove.isPending} onClose={() => setConfirmingDelete(false)} onConfirm={() => remove.mutate()} />
      </div>
      <WyscoutPlayerMatchDrawer
        open={selectedMatchId !== null}
        detail={fixtureAccessInvalidated ? undefined : fixtureDetail.data}
        loading={fixtureDetail.isPending && fixtureDetail.fetchStatus !== 'idle'}
        error={fixtureDetail.isError ? ((fixtureDetail.error as {message?: string})?.message || 'Unable to load player match.') : null}
        onClose={() => removeSelectedMatch()}
        onRetry={() => { void fixtureDetail.refetch(); }}
        fullMatchHref={selectedMatchId ? `${root}/${encodeURIComponent(id)}/wyscout-matches/${selectedMatchId}?${fullMatchSearch.toString()}` : undefined}
        onRestoreFocus={restoreFixtureFocus}
        refreshAnnouncement={fixtureRefreshAnnouncement}
      />
    </>
  );
};

export default PlayerDetailPage;
